/**
 * Assessment Schedules Hooks
 *
 * TanStack Query hooks for assessment schedule CRUD operations.
 * Assessment schedules enable automatic spot-check assessments.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// TYPES
// ============================================

export type ScheduleType = 'daily' | 'weekly' | 'shift';
export type SelectionMode = 'random' | 'round_robin' | 'all';

export interface AssessmentSchedule {
  id: string;
  orgId: string;
  name: string;
  description: string | null;
  sopId: string;
  cameraId: string | null;
  departmentId: string | null;
  scheduleType: ScheduleType;
  frequency: number;
  startHour: number;
  endHour: number;
  daysOfWeek: string;
  timezone?: string;
  selectionMode: SelectionMode;
  isActive: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined fields
  sopTitle?: string;
  departmentName?: string | null;
  cameraName?: string | null;
  // Phase 12.3: Template source type
  sourceType?: 'sop' | 'ai_generated' | 'checklist' | 'kpi';
  checklistId?: string | null;
  kpiConfigId?: string | null;
  // Phase 24.3: KPI-only templates
  kpiDefinitionId?: string | null;
  kpiDefinitionName?: string | null;
  // Phase 34: target lens for multi-sensor cameras (NULL = lens 1)
  sourceNumber?: number | null;
  // Phase 36.4: Job (section) allowlist narrowing the analysis candidate space.
  // NULL = whole SOP, which is every pre-36.4 template.
  scopeSectionIds?: string[] | null;
}

export interface CreateAssessmentSchedule {
  name: string;
  description?: string;
  sopId?: string; // Phase 12.3: Optional — required only for 'sop' sourceType
  cameraId?: string;
  departmentId?: string;
  scheduleType: ScheduleType;
  frequency?: number;
  startHour?: number;
  endHour?: number;
  daysOfWeek?: string;
  timezone?: string;
  selectionMode?: SelectionMode;
  isActive?: boolean;
  // Phase 12.3: Template source type (Phase 24.3: added 'kpi')
  sourceType?: 'sop' | 'ai_generated' | 'checklist' | 'kpi';
  checklistId?: string;
  kpiConfigId?: string;
  kpiDefinitionId?: string; // Phase 24.3: KPI-only templates
  // Phase 34: target lens for multi-sensor cameras. On update: number = target
  // that lens, null = clear (server stores NULL = lens 1), undefined = keep stored.
  sourceNumber?: number | null;
  // Phase 36.4: Job (section) allowlist. Same tri-state as sourceNumber above:
  // array = set, null = clear (whole SOP — the documented rollback lever),
  // undefined = keep stored. NEVER send [] — the API rejects it with a 400
  // (Zod .min(1)) so that "scope to nothing" cannot be confused with "no scope".
  scopeSectionIds?: string[] | null;
}

export interface UpdateAssessmentSchedule extends Partial<CreateAssessmentSchedule> {}

export interface PaginatedSchedules {
  data: AssessmentSchedule[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================
// QUERY KEYS
// ============================================

export const scheduleKeys = {
  all: ['assessment-schedules'] as const,
  lists: () => [...scheduleKeys.all, 'list'] as const,
  list: (filters: { page?: number; limit?: number }) =>
    [...scheduleKeys.lists(), filters] as const,
  details: () => [...scheduleKeys.all, 'detail'] as const,
  detail: (id: string) => [...scheduleKeys.details(), id] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Fetch assessment schedules list with pagination
 *
 * Note: The API returns { success, data: [...], pagination: {...} } but useApi
 * unwraps the { success, data } envelope, so we receive just the array directly.
 */
export function useAssessmentSchedules(options?: { page?: number; limit?: number }) {
  const api = useApi();

  return useQuery({
    queryKey: scheduleKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));

      const queryString = params.toString();
      const endpoint = queryString
        ? `/api/assessment-schedules?${queryString}`
        : '/api/assessment-schedules';

      const response = await api.get<AssessmentSchedule[]>(endpoint);
      return response;
    },
  });
}

/**
 * Fetch single schedule details
 *
 * Note: useApi unwraps the { success, data } envelope, so the result is the schedule directly.
 */
export function useAssessmentSchedule(id: string) {
  const api = useApi();

  return useQuery({
    queryKey: scheduleKeys.detail(id),
    queryFn: () => api.get<AssessmentSchedule>(`/api/assessment-schedules/${id}`),
    enabled: !!id,
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Create schedule mutation
 */
export function useCreateSchedule() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAssessmentSchedule) =>
      api.post<AssessmentSchedule>('/api/assessment-schedules', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

/**
 * Update schedule mutation
 */
export function useUpdateSchedule() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAssessmentSchedule }) =>
      api.patch<AssessmentSchedule>(`/api/assessment-schedules/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(variables.id) });
    },
  });
}

/**
 * Delete schedule mutation
 */
export function useDeleteSchedule() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ message: string }>(`/api/assessment-schedules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

/**
 * Pause schedule mutation
 */
export function usePauseSchedule() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<AssessmentSchedule>(`/api/assessment-schedules/${id}/pause`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(id) });
    },
  });
}

/**
 * Resume schedule mutation
 */
export function useResumeSchedule() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<AssessmentSchedule>(`/api/assessment-schedules/${id}/resume`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(id) });
    },
  });
}

// ============================================
// HELPERS
// ============================================

/**
 * Convert days of week string to array of day numbers
 * "1,2,3,4,5" -> [1, 2, 3, 4, 5]
 */
export function parseDaysOfWeek(daysStr: string): number[] {
  return daysStr.split(',').map(Number);
}

/**
 * Convert array of day numbers to days of week string
 * [1, 2, 3, 4, 5] -> "1,2,3,4,5"
 */
export function formatDaysOfWeek(days: number[]): string {
  return days.sort((a, b) => a - b).join(',');
}

/**
 * Get day name from day number (0 = Sunday, 1 = Monday, etc.)
 */
export function getDayName(day: number): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[day] ?? '';
}

/**
 * Format schedule type for display
 */
export function formatScheduleType(type: ScheduleType): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

/**
 * Format selection mode for display
 */
export function formatSelectionMode(mode: SelectionMode): string {
  switch (mode) {
    case 'random':
      return 'Random';
    case 'round_robin':
      return 'Round Robin';
    case 'all':
      return 'All Operators';
    default:
      return mode;
  }
}

/**
 * Format time window for display
 */
export function formatTimeWindow(startHour: number, endHour: number, timezone?: string): string {
  const formatHour = (h: number) => {
    if (h === 24) return 'Midnight'; // endHour 24 = end of day
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour}${suffix}`;
  };

  let timeWindow = `${formatHour(startHour)} - ${formatHour(endHour)}`;

  // Add timezone abbreviation if provided
  if (timezone) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(new Date());
      const tzAbbr = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
      if (tzAbbr) {
        timeWindow += ` ${tzAbbr}`;
      }
    } catch {
      // Invalid timezone, skip abbreviation
    }
  }

  return timeWindow;
}
